// A passing module's view of the shared pass selection (engines/event-link.ts, Ben 2026-10-21).
// `linked` = the picks made in OTHER modules that touch this module's passes (same player), combined
// (Ben, 2026-10-26: "filtered, not replaced"): only passes in all of them stay bright, hoverable and
// counted, one blue chip per pick. This module's own pick is never dropped by another module's.
// `onReleased` runs when this module's own pick is cleared from outside (a blue chip's ✕ elsewhere),
// so it drops its own highlight too.

import { useEffect, useMemo, useRef, useState } from "react";
import type { ProtoEvent } from "@catbento/shared";
import { currentPassSelections, subscribePassSelection, type PassSelection } from "../engines/event-link.js";

export interface LinkedPick {
  sel: PassSelection;
  /** The module it came from, as its header reads ("Passing Report"). */
  from: string;
}

export interface LinkedSelection {
  picks: LinkedPick[];
  /** The passes in every one of the picks. */
  ids: ReadonlySet<string>;
}

export function useLinkedSelection(mod: HTMLElement, passes: readonly ProtoEvent[], onReleased?: () => void): LinkedSelection | null {
  const [sels, setSels] = useState<PassSelection[]>(currentPassSelections);
  const onReleasedRef = useRef(onReleased);
  onReleasedRef.current = onReleased;
  const ownRef = useRef(currentPassSelections().some((s) => s.source === mod));
  useEffect(
    () =>
      subscribePassSelection((next) => {
        const hadOwn = ownRef.current;
        ownRef.current = next.some((s) => s.source === mod);
        setSels(next);
        if (hadOwn && !ownRef.current) onReleasedRef.current?.();
      }),
    [mod],
  );
  return useMemo(() => {
    const picks: LinkedPick[] = [];
    const sets: Set<string>[] = [];
    for (const sel of sels) {
      if (sel.source === mod) continue;
      const own = new Set(sel.ids);
      // A pick on another player's module leaves this one alone.
      if (!passes.some((p) => own.has(p.id))) continue;
      const from = sel.source.querySelector(".module-title")?.textContent?.trim() || "another module";
      picks.push({ sel, from });
      sets.push(own);
    }
    const [first, ...rest] = sets;
    if (!first) return null;
    const ids = new Set([...first].filter((id) => rest.every((s) => s.has(id))));
    return { picks, ids };
  }, [sels, mod, passes]);
}
